import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SFDatagrid,
  SFActionBar,
  SFPageContainer,
  ACTION_BAR_ACTION_TYPES,
  ACTION_BAR_ACTION_NAMES,
} from "@sf-core";
import { PATHS } from "../../constants/paths";
import { GridNames } from "../../constants/gridConfig";
import { useSelector } from "react-redux";
import getNavDefs from "../navDefs/getBenchmarkNavDefs";
import { API_PARAM } from "../../constants/apiParams";
import {
  exportBenchmarkJFAGrid,
  uploadBenchmarkJFA,
} from "../../services/benchmarkJfaService";
import { Claims } from "../../constants/claims";

const BenchmarkJFA = () => {
  const handleJFADownload = () => {
    exportBenchmarkJFAGrid(JFAGridLayout?.filterModel ?? {});
  };

  const JFAGridLayout = useSelector((state) =>
    state.gridLayout.find((grid) => grid.gridName === GridNames.BENCHMARK_JFA)
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const navDefs = getNavDefs({ t });

  const columnDefs = useMemo(
    () => [
      {
        headerName: t("benchmark.jfaPathsGrid.rowNumber"),
        field: "rowNumber",
        filter: true,
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.specializedOccupationId"),
        field: "specializedOccupationId",
        filter: true,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.specializedOccupationName"),
        field: "specializedOccupationName",
        filter: true,
        filterParams: {
          order: 2,
        },
      },
      {
        headerName: t("benchmark.jfaPathsGrid.businessCapabilityName"),
        field: "businessCapabilityCode",
        hide: false,
        filter: true,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.businessCapabilityCode"),
        field: "businessCapabilityName",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.businessCapabilityDescription"),
        field: "businessCapabilityDescription",
        filter: true,
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.disciplineName"),
        field: "disciplineName",
        filter: true,
        filterParams: {
          order: 1,
        },
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.disciplineCode"),
        field: "disciplineCode",
        filter: true,
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.disciplineDescription"),
        field: "disciplineDescription",
        filter: true,
        filterParams: {
          order: 2,
        },
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobFamilyName"),
        field: "jobFamilyName",
        filter: true,
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobFamilyCode"),
        field: "jobFamilyCode",
        filter: true,
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobFamilyDescription"),
        field: "jobFamilyDescription",
        filter: true,
        hide: false,
        filterParams: {
          order: 3,
        },
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobRoleName"),
        field: "jobRoleName",
        filter: true,
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobRoleCode"),
        field: "jobRoleCode",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobRoleDescription"),
        field: "jobRoleDescription",
        filter: true,
        hide: false,
        filterParams: {
          order: 4,
        },
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobRoleLevel"),
        field: "jobRoleLevel",
        filter: true,
        hide: false,
        filterParams: {
          order: 5,
        },
      },
      {
        headerName: t("benchmark.jfaPathsGrid.careerLevel"),
        field: "careerLevel",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.radfordLevel"),
        field: "radfordLevel",
        hide: false,
        filterParams: {
          order: 6,
        },
      },
      {
        headerName: t("benchmark.jfaPathsGrid.roleType"),
        field: "roleType",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.careerTrack"),
        field: "careerTrack",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.SOC"),
        field: "soc",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.active"),
        field: "active",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.definitionSource"),
        field: "definitionSource",
        hide: false,
      },
      {
        headerName: t("benchmark.jfaPathsGrid.jobTitleIDs"),
        field: "jobTitleIDs",
        hide: false,
      },
    ],
    []
  );

  const actionDefs = [
    {
      name: ACTION_BAR_ACTION_NAMES.ADD,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      permissions: [Claims.AdminBenchmarkJFAImport],
      onClick: () => navigate(PATHS.BENCHMARK_JFA_PATH_ADD),
      buttonProps: {
        label: t("benchmark.addButton.addBenchmarkJfa"),
        variant: "primary",
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.DOWNLOAD,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      permissions: [Claims.AdminBenchmarkJFAExport],
      onClick: handleJFADownload,
      iconProps: {
        tooltip: t("benchmark.download.downloadJFA"),
        overflowLabel: t("benchmark.download.downloadJFA"),
      },
    },
    {
      name: "validateJFA",
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      permissions: [Claims.AdminBenchmarkJFAUpload],
      onClick: uploadBenchmarkJFA,
      buttonProps: {
        label: t("benchmark.validate.jfa"),
        variant: "primary",
        async: true,
      },
      confirmation: true,
      confirmationProps: {
        header: t("benchmark.validateButton.validateHeader"),
        message: t("benchmark.validateButton.validateMessage"),
        primaryLabel: t("benchmark.validateButton.validateConfirm"),
        secondaryLabel: t("benchmark.validateButton.validateCancel"),
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.FILTER,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      filterParams: {
        gridName: GridNames.BENCHMARK_JFA,
      },
    },
  ];

  return (
    <SFPageContainer disableGutters>
      <SFActionBar
        titleProps={{
          title: t("benchmark.titles.benchmarkJFA"),
        }}
        actionDefs={actionDefs}
        navDefs={navDefs}
      />
      <SFDatagrid
        canDownload={false}
        columnDefs={columnDefs}
        gridName={GridNames.BENCHMARK_JFA}
        datasourceUrl={API_PARAM.BENCHMARK_JFA_GRID}
        filterValuesUrl={API_PARAM.BENCHMARK_JFA_COL_FILTER}
        basicFiltering
      />
    </SFPageContainer>
  );
};

export default BenchmarkJFA;
